import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { setPersonProperties } from '@/lib/analytics';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditsContext = createContext({
  balance: 0,
  tier: 'free',
  monthlyCredits: 0,
  lifetimeUsed: 0,
  availableModels: [],
  costs: {},
  loading: true,
  refresh: () => {},
});

export function CreditsProvider({ children }) {
  const [credits, setCredits] = useState({
    balance: 0,
    tier: 'free',
    monthlyCredits: 0,
    lifetimeUsed: 0,
    availableModels: [],
    loading: true,
  });
  const [costs, setCosts] = useState({});

  const fetchCredits = useCallback(async () => {
    try {
      const token = localStorage.getItem('orbis_token');
      if (!token) return;

      const resp = await axios.get(`${API_URL}/credits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCredits({
        balance: resp.data.balance,
        tier: resp.data.tier,
        monthlyCredits: resp.data.monthly_credits,
        lifetimeUsed: resp.data.lifetime_used,
        availableModels: resp.data.available_models || [],
        loading: false,
      });
      setPersonProperties({ tier: resp.data.tier, credits_balance: resp.data.balance, monthly_credits: resp.data.monthly_credits });
    } catch (err) {
      // Not logged in or server error — set defaults
      setCredits(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchCosts = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/credits/costs`);
      setCosts(resp.data.costs || {});
    } catch {
      // Non-critical
    }
  }, []);

  // Set up response interceptor — refresh credits on 402 AND after any successful POST/GET that might cost credits
  const interceptorRef = useRef(null);
  useEffect(() => {
    if (interceptorRef.current !== null) return; // Only set up once
    interceptorRef.current = axios.interceptors.response.use(
      (resp) => {
        // Refresh balance after any successful action that likely costs credits
        const url = resp.config?.url || '';
        const method = (resp.config?.method || '').toUpperCase();
        const creditEndpoints = ['/build', '/generate', '/edit', '/analyze', '/chat', '/image-slots'];
        if (method === 'POST' && creditEndpoints.some(ep => url.includes(ep))) {
          setTimeout(fetchCredits, 1000); // Slight delay so DB has committed
        }
        return resp;
      },
      (error) => {
        if (error.response?.status === 402) {
          toast.error(error.response?.data?.detail || 'Insufficient credits — upgrade your plan.');
          fetchCredits(); // Refresh balance display
        }
        return Promise.reject(error);
      }
    );
  }, [fetchCredits]);

  // Fetch on mount + poll every 30 seconds to stay current
  useEffect(() => {
    fetchCredits();
    fetchCosts();
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [fetchCredits, fetchCosts]);

  return (
    <CreditsContext.Provider
      value={{
        ...credits,
        costs,
        refresh: fetchCredits,
      }}
    >
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  return useContext(CreditsContext);
}
